import React, { useState } from "react";
import { Colors, Classes, Icon } from "@blueprintjs/core";
import { Tooltip2 } from "@blueprintjs/popover2";
import styled from "styled-components";
import { Avatar, Box, quarks, Stack, useToggle } from "ui-components";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { userQuery } from "ui-io/queries/users/user-query";
import { AssetReferenceT, TodoNoteTagT, UrlExternalReferenceT } from "ui-io/task-io";
import {
  taskPinCommentMutation,
  taskUnPinCommentMutation,
} from "ui-io/queries/tasks/task-pin-comment";
import { LIST_COMPANY_NOTES } from "ui-io/queries/configs/get-brand-notes";
import { unpinBrandNotes } from "ui-io/queries/configs/unpin-brand-notes";
import { IconNames } from "@blueprintjs/icons";
import { TaskT } from "ui-io/task-io/task-t";

import Attachment from "./attachment";
import { ActiveAttachmentT } from ".";
import normalizeUserQueryData from "../../../utils/normalize-user-query-data";
import toasts from "../../../utils/toasts";
import { TASKS_QUERY_FILTER } from "ui-io/queries/tasks/task-query";

type Props = {
  isUsersComment: boolean;
  task: TaskT;
  isPinned?: boolean;
  standardTimestampFormat: string;
  timeAgo: string;
  attachments?: UrlExternalReferenceT[];
  username: string;
  text: string;
  isMock?: boolean;
  handleToggleIsAttachmentOpen?: (nextAttachment: ActiveAttachmentT) => void;
  onDelete: (attachmentId: string | undefined) => void;
  uploadTimeStamp: number;
  tags?: TodoNoteTagT[];
  noteId: string;
};
const PINNED_TAG = "pinned";
const EVENT_TAG = "event_type";
const Comment = (props: Props) => {
  const {
    task,
    isUsersComment,
    attachments,
    username,
    text,
    timeAgo,
    standardTimestampFormat,
    isMock,
    handleToggleIsAttachmentOpen,
    onDelete,
    uploadTimeStamp,
    tags,
    noteId,
    isPinned,
  } = props;
  const { data, isLoading, isError } = useQuery(userQuery(username));
  const { email, displayName } = normalizeUserQueryData({
    data,
    username,
  });
  const queryClient = useQueryClient();
  const Wrapper = isUsersComment ? RightComment : LeftComment;

  // const isAlreadyPinned = tags?.some((ele) => ele.key === PINNED_TAG) ?? false;
  const shouldBePinned = tags?.every((ele) => ele.key !== EVENT_TAG) ?? true;

  const [showPin, setShowPin] = useState(false);

  const pinCommentMutation = useMutation(taskPinCommentMutation, {
    onSuccess: () => {
      toasts.success("Successfully pinned the comment!");
      queryClient.refetchQueries([TASKS_QUERY_FILTER, task._raw.task_id]);
      queryClient.refetchQueries(["LIST_COMPANY_NOTES"]);
    },
    onError: () => {
      toasts.error("Failed to pin the comment!");
    },
  });
  const unPinCommentMutation = useMutation(taskUnPinCommentMutation, {
    onSuccess: () => {
      toasts.success("Successfully un-pinned the comment!");
      queryClient.refetchQueries([TASKS_QUERY_FILTER, task._raw.task_id]);
      queryClient.refetchQueries(["LIST_COMPANY_NOTES"]);
    },
    onError: () => {
      toasts.error("Failed to un-pin the comment!");
    },
  });
  const unPinBrandCommentMutation = useMutation(unpinBrandNotes, {
    onSuccess: () => {
      toasts.success("Successfully un-pinned the comment!");
      queryClient.refetchQueries([LIST_COMPANY_NOTES]);
    },
    onError: () => {
      toasts.error("Failed to un-pin the brand notes!");
    },
  });

  const handlePinComments = () => {
    const noteToPin = {
      noteId: noteId,
      companyID: task._raw.companyID || "",
      taskId: task._raw.task_id,
      user: username,
      timestamp: uploadTimeStamp,
      tags,
      text,
    };
    if (attachments) {
      // let notesAttachments = task?.notes?.filter(
      //   (note) => note.timestamp == uploadTimeStamp && note.text !== text
      // );
      // notesAttachments?.length &&
      //   notesAttachments.forEach((attachment) => {
      //     if (attachment && attachment.text) {
      //       // noteToPin.push({
      //       //   // noteId: attachment.noteId,
      //       //   companyID: attachment.companyID,
      //       //   taskId: attachment.taskId,
      //       //   user: username,
      //       //   // timestamp: attachment.timestamp,
      //       //   tags: attachment?.tags,
      //       //   text: attachment.text,
      //       // });
      //     }
      //   });
    }
    if (!isPinned) {
      pinCommentMutation.mutate({ noteId });
    } else {
      // if (task?.isCompleted && task.configUuid) {
      //   unPinBrandCommentMutation.mutate({
      //     configurationId: task.configUuid as string,
      //     note: noteToPin,
      //   });
      // }
      unPinCommentMutation.mutate({ noteId });
    }
  };

  const getAttachments = () => {
    const result =
      attachments &&
      attachments.map((attachment, index) => {
        return (
          <Attachment
            key={index}
            attachment={attachment}
            isUsersComment={isUsersComment}
            handleToggleIsAttachmentOpen={handleToggleIsAttachmentOpen}
            onDelete={onDelete}
            uploadedUser={username}
            uploadTimeStamp={uploadTimeStamp}
          />
        );
      });
    return <>{result}</>;
  };
  if (isMock) {
    return (
      <>
        <RightComment isPinned={true}>
          <Box flexDir="row" justify="between" mb="xsmall">
            <TinyText style={{ marginRight: 6 }} className={Classes.SKELETON}>
              You
            </TinyText>
            <TinyText className={Classes.SKELETON}>{timeAgo}</TinyText>
          </Box>
          <MultilineDiv className={Classes.SKELETON}>
            {text || "Loading..."}
          </MultilineDiv>
        </RightComment>
        <Avatar
          displayName={displayName || username}
          isCurrentUser={true}
          isLoading={true}
        />
      </>
    );
  }
  return (
    <>
      <Wrapper
        isPinned={isPinned ?? false}
        onMouseEnter={() => {
          shouldBePinned && setShowPin(true);
        }}
        onMouseLeave={() => {
          shouldBePinned && setShowPin(false);
        }}
        className={
          pinCommentMutation.isLoading
            ? Classes.SKELETON
            : Classes.FOCUS_DISABLED
        }
      >
        {(isPinned || (showPin && !task.isCompleted)) && (
          <Pinned onClick={handlePinComments}>
            <Icon icon={IconNames.PIN} />
          </Pinned>
        )}
        <Box flexDir="row" justify="between" mb="xsmall">
          <Tooltip2 content={email} disabled={isLoading || isError}>
            <TinyText
              style={{
                marginRight: 6,
                ...(isError && { fontStyle: "italic" }),
              }}
              className={isLoading ? Classes.SKELETON : ""}
            >
              {isUsersComment ? "You" : isError ? username : displayName}
            </TinyText>
          </Tooltip2>
          <Tooltip2 content={standardTimestampFormat}>
            <TinyText>{timeAgo}</TinyText>
          </Tooltip2>
        </Box>
        <Box
          mb={text && !!attachments?.length ? "small" : 0}
          style={{ whiteSpace: "pre-wrap", wordWrap: "break-word" }}
        >
          {text}
        </Box>
        <Stack spacing={quarks.margins.small}>
          {attachments && getAttachments()}
        </Stack>
      </Wrapper>
      <Avatar
        email={email || "yantrix@gmail.com"}
        profileUrl={""}
        onlyAvatar={true}
        displayName={displayName || username}
        isCurrentUser={isUsersComment}
        isLoading={isLoading}
        size={"30px"}
        position="end"
      />
    </>
  );
};

export default Comment;

type CommentTypeT = "LEFT" | "RIGHT";
const colorPicker: Record<CommentTypeT, { DARK: string; LIGHT: string }> = {
  LEFT: {
    DARK: Colors.DARK_GRAY5,
    LIGHT: Colors.WHITE,
  },
  RIGHT: {
    DARK: Colors.DARK_GRAY2,
    LIGHT: Colors.LIGHT_GRAY5,
  },
};
const bgColor = (
  commentType: CommentTypeT,
  isPinned: boolean,
  darkMode?: boolean
) =>
  isPinned
    ? Colors.BLUE3
    : colorPicker[commentType][darkMode ? "DARK" : "LIGHT"];

const textColor = (isPinned: boolean, darkMode?: boolean) =>
  isPinned
    ? Colors.LIGHT_GRAY5
    : darkMode
      ? Colors.LIGHT_GRAY5
      : Colors.DARK_GRAY5;

const TinyText = styled.div`
  font-size: 10px;
  font-weight: 700;
  margin-bottom: 6px;
`;
const Default = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  border-style: solid;
  border-width: 1px;
  padding: 14px;
  max-width: 400px;
  font-size: 12px;
  width: fit-content;
  ${TinyText} & {
    color: ${(props) => {
      return props.theme.darkMode ? Colors.LIGHT_GRAY5 : Colors.GRAY1;
    }};
`;
const MultilineDiv = styled.div`
  white-space: pre-wrap;
`;

const LeftComment = styled(Default)<{
  isPinned: boolean;
}>`
  background-color: ${(props) =>
    bgColor("LEFT", props.isPinned, props.theme.darkMode)};
  border-color: ${(props) => {
    return props.theme.darkMode ? Colors.DARK_GRAY3 : Colors.LIGHT_GRAY4;
  }};
  color: ${(props) => textColor(props.isPinned, props.theme.darkMode)};
  border-radius: 12px 12px 12px 0;
`;

const RightComment = styled(Default)<{
  isPinned: boolean;
}>`
  background-color: ${(props) =>
    bgColor("RIGHT", props.isPinned, props.theme.darkMode)};
  border-color: ${(props) => {
    return props.theme.darkMode ? Colors.DARK_GRAY3 : Colors.LIGHT_GRAY2;
  }};
  color: ${(props) => textColor(props.isPinned, props.theme.darkMode)};
  border-radius: 12px 12px 0 12px;
  }
`;
const Pinned = styled.div`
  padding-left: 1%;
  z-index: 0;
  position: absolute;
  right: 0;
  top: 0;
  color: ${Colors.RED5};
  &:hover {
    cursor: pointer;
    transform: scale(1.2);
  }
`;
